import Model from "../Model.js";

/**
 * `ExcludedAccountGroup` represents an account excluded from a row in
 * the report editor. It can handle both known groups and langage specific
 * account groups from the chart of accounts. It can return the account children
 * as excluded account groups as well to facilitate the tree construction.
 * Needs an id and a chart of accounts.
 *
 * @param {string} id - Unique identifier of the group.
 * @param {chartOfAccounts} chartOfAccounts - Chart of accounts
 * containing the new account group.
 */
export default class ExcludedAccountGroup extends Model {
	constructor({ id, chartOfAccounts }) {
		super(...arguments);

		this._id = id;
		this._chartOfAccounts = chartOfAccounts;

		this._accountGroupInChartOfAccounts =
			this._getAccountGroupInChartOfAccounts();

		// rendering its id if there is nothing to render.
		this._name = this.isInChartOfAccounts()
			? this._accountGroupInChartOfAccounts.getName().currentTranslation()
			: id;
	}

	getId() {
		return this._id;
	}

	getName() {
		return this._name;
	}

	getChildren() {
		return this.isInChartOfAccounts() ? this._getChildren() : [];
	}

	isInChartOfAccounts() {
		return !!this._accountGroupInChartOfAccounts;
	}

	isEquivalentTo(accountGroup) {
		return (
			this.getId() === accountGroup.getId() ||
			this.getName() === accountGroup.getName()
		);
	}

	/**
	 * returns undefined if there is no account group with given id.
	 */
	_getAccountGroupInChartOfAccounts() {
		return this._chartOfAccounts.findById(this._id);
	}

	_getChildren() {
		if (!this.isInChartOfAccounts()) {
			return [];
		}

		return this._accountGroupInChartOfAccounts
			.getChildren()
			.filter((child) => child.isAccountGroup())
			.map((child) => this._makeExcludedAccountGroup(child));
	}

	_makeExcludedAccountGroup(accountGroup) {
		return new this.constructor({
			id: accountGroup.getId(),
			chartOfAccounts: this._chartOfAccounts,
		});
	}
}
